import { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { useQueryClient } from "@tanstack/react-query";
import { AppleMap } from "capitalrx-components-2.0";
import { Button, Color, HorizontalLine, Link, LoadingGroup, LoadingSkeleton, Panel, Tag } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Spacing, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading, Semibold, Subheading, Subtitle, TextColor } from "capitalrx-components-2.0/lib/typography";
import { formatDollars } from "capitalrx-utilities/lib/formatters";
import { DateTime } from "luxon";

import { useGetMedicalClaimDetails } from "@/App/api/claims";
import { ContentPanel, PageWrapper } from "@/App/components";
import { NavigateButton } from "@/App/components/NavigateButton";
import { StackOrShelf } from "@/App/components/StackOrShelf";
import { MemberEligibilityContext } from "@/App/context";
import { useNavigateClientNewTab } from "@/App/hooks";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";

import { ClaimInfoStack } from "../Claims";
import { ClaimsDetailServicePanel } from "./ClaimsDetailServicePanel";

import "./ClaimsDetails.scss";

interface ProviderAddress {
  readonly addressLine1?: string | null | undefined;
  readonly addressLine2?: string | null | undefined;
  readonly state?: string | null | undefined;
  readonly city?: string | null | undefined;
  readonly zipCode?: string | null | undefined;
}

const MAPS_LINK = process.env.MAPS_LINK || "https://maps.apple.com/?q=";

export const MedicalClaimDetailsPage = () => {
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);

  const { id: claimId, personCode: personCode } = useParams();

  const { t: translate } = useTranslation();

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const handlePrintButtonClick = () => {
    const searchParams = new URLSearchParams();
    searchParams.append("claimId", claimId as string);

    if (isViewingDataholder) {
      searchParams.append("member", memberEligibility.member.id.toString());
    }
    if (medicalClaimData?.person_code) {
      searchParams.append("personCode", medicalClaimData?.person_code);
    }

    logEvent(ANALYTIC_EVENTS.CLAIM_DETAILS.printMedicalClaimDetails);
    navigateNewTab("/print-medical-claim-details", searchParams);
  };

  const onBackPressed = useCallback(() => {
    logEvent(ANALYTIC_EVENTS.CLAIM_HISTORY.backButton);
    queryClient.removeQueries({ queryKey: ["medicalClaimDetails"] });
    navigate("/claims-history?claims_type=medical");
  }, [navigate, queryClient]);

  const navigateNewTab = useNavigateClientNewTab();

  const { cardColumns, stackColumns, spacing } = useResponsive({
    cardColumns: {
      desktop: 6,
      tablet: 6,
      mobile: 12,
    },
    stackColumns: {
      desktop: 6,
      tablet: 12,
      mobile: 12,
    },
    spacing: {
      desktop: "large",
      tablet: "large",
      mobile: "mini",
    },
  });

  const { data: medicalClaimData, isLoading: isLoadingMedicalClaimData } = useGetMedicalClaimDetails(
    { id: +(claimId as string), person_code: personCode },
    !!claimId,
  );

  const getFormattedDate = (date?: string | null | undefined) => {
    if (!date) {
      return "";
    }
    return DateTime.fromISO(date, { zone: "utc" }).toFormat("DDD");
  };

  const getDisplayableAddress = (address: ProviderAddress) => {
    const { addressLine1, addressLine2, city, state, zipCode } = address;

    return (
      <>
        {addressLine1 || ""}
        {addressLine2 ? <>, {addressLine2}</> : ""}
        {city && state && zipCode ? (
          <>
            <br />
            {city}, {state} {zipCode}
          </>
        ) : (
          ""
        )}
      </>
    );
  };

  const getAddressQueryString = (address: ProviderAddress) => {
    const addressValues = [address.addressLine1, address.addressLine2, address.city, address.state, address.zipCode];
    return addressValues.filter((value) => !!value).join(",") || "";
  };

  const getAddressLink = (address: ProviderAddress, query?: string) => {
    query = query || getAddressQueryString(address);

    if (!query) return <></>;

    return <Link to={`${MAPS_LINK}${encodeURIComponent(query || getAddressQueryString(address))}`}>{getDisplayableAddress(address)}</Link>;
  };

  const getProviderName = (firstName?: string | null | undefined, lastNameOrOrganizationalName?: string | null | undefined) => {
    if (firstName) {
      return `${firstName} ${lastNameOrOrganizationalName}`;
    }
    return lastNameOrOrganizationalName;
  };

  let statusTagElement = <></>;
  if (medicalClaimData?.status === "denied") {
    statusTagElement = <Tag type="error-high-contrast">{translate("CLAIMS.statusOptions.denied")}</Tag>;
  } else if (medicalClaimData?.status === "partially_paid") {
    statusTagElement = <Tag type="info-high-contrast">{translate("CLAIMS.statusOptions.partiallyApproved")}</Tag>;
  } else if (medicalClaimData?.status === "pended") {
    statusTagElement = <Tag type="warning-high-contrast">{translate("CLAIMS.statusOptions.inProgress")}</Tag>;
  } else if (medicalClaimData?.status === "paid") {
    statusTagElement = <Tag type="success-high-contrast">{translate("CLAIMS.statusOptions.approved")}</Tag>;
  }

  const formattedServiceDate = getFormattedDate(medicalClaimData?.minimum_service_date);
  const formattedReceivedDate = getFormattedDate(medicalClaimData?.submission_date);
  const copayCoinsuranceAmount = (medicalClaimData?.total_amount_of_coinsurance || 0) + (medicalClaimData?.total_amount_of_copay || 0);

  const billingProviderName = getProviderName(
    medicalClaimData?.billing_provider_first_name,
    medicalClaimData?.billing_provider_last_name_or_organizational_name,
  );
  const billingAddress = getDisplayableAddress({
    addressLine1: medicalClaimData?.billing_provider_address_line_1,
    addressLine2: medicalClaimData?.billing_provider_address_line_2,
    state: medicalClaimData?.billing_provider_state,
    city: medicalClaimData?.billing_provider_city,
    zipCode: medicalClaimData?.billing_provider_zip,
  });

  const renderingProviderName = getProviderName(
    medicalClaimData?.rendering_provider_first_name,
    medicalClaimData?.rendering_provider_last_name_or_organizational_name,
  );
  const renderingProviderAddressLink = getAddressLink({
    addressLine1: medicalClaimData?.rendering_provider_address_line_1,
    addressLine2: medicalClaimData?.rendering_provider_address_line_2,
    state: medicalClaimData?.rendering_provider_state,
    city: medicalClaimData?.rendering_provider_city,
    zipCode: medicalClaimData?.rendering_provider_zip,
  });
  const serviceFacilityAddressQuery = getAddressQueryString({
    addressLine1: medicalClaimData?.service_facility_location_address_line_1,
    addressLine2: medicalClaimData?.service_facility_location_address_line_2,
    state: medicalClaimData?.service_facility_location_address_state,
    city: medicalClaimData?.service_facility_location_address_city,
    zipCode: medicalClaimData?.service_facility_location_address_zip,
  });
  const serviceFacilityAddressLink = getAddressLink(
    {
      addressLine1: medicalClaimData?.service_facility_location_address_line_1,
      addressLine2: medicalClaimData?.service_facility_location_address_line_2,
      state: medicalClaimData?.service_facility_location_address_state,
      city: medicalClaimData?.service_facility_location_address_city,
      zipCode: medicalClaimData?.service_facility_location_address_zip,
    },
    serviceFacilityAddressQuery,
  );

  let totalBilled = 0;
  let totalAllowedByPlan = 0;
  let totalPlanPaid = 0;
  let totalPatientPay = 0;

  if (medicalClaimData?.status !== "pended" && !!medicalClaimData?.service_line_items?.length) {
    for (const lineItem of medicalClaimData.service_line_items) {
      totalBilled = totalBilled + (lineItem.line_item_charge_amount || 0);
      totalAllowedByPlan = totalAllowedByPlan + (lineItem.repriced_allowed_amount || 0);
      totalPlanPaid = totalPlanPaid + (lineItem.plan_pay_amount || 0);
      totalPatientPay = totalPatientPay + (lineItem.patient_pay_amount || 0);
    }
  }

  const directMemberReimbursementIndicator =
    (medicalClaimData?.direct_member_reimbursement_indicator ?? "0") !== "0" ? (
      <Body>{translate("CLAIMS.claimsDetailsPage.reimbursementClaim")}</Body>
    ) : null;

  return (
    <PageWrapper>
      <ContentPanel
        title={
          <StackOrShelf alignment="top" spacing="mini">
            <Stack spacing="mini">
              <Shelf spacing="mini">
                <NavigateButton onClick={onBackPressed}>{translate("CLAIMS.claimsDetailsPage.back")}</NavigateButton>
                <Shelf.Spacer />
              </Shelf>
              <LoadingGroup loading={isLoadingMedicalClaimData}>
                <LoadingGroup.Placeholder
                  fallback={
                    <>
                      <LoadingSkeleton style={{ height: "30px", width: "250px", maxWidth: "100%" }} />
                      <LoadingSkeleton style={{ height: "20px", width: "150px" }} />
                      <LoadingSkeleton style={{ height: "20px", width: "250px", maxWidth: "100%" }} />
                    </>
                  }
                >
                  <Shelf>
                    <Subtitle testId="claim-title">{billingProviderName}</Subtitle>
                    <Shelf.Spacer />
                  </Shelf>
                  <StackOrShelf spacing={spacing as Spacing} alignment="left">
                    <Body>
                      {medicalClaimData?.network === "in_network_tier_1" && translate("CLAIMS.networkOptions.inNetwork")}
                      {medicalClaimData?.network === "out_of_network" && translate("CLAIMS.networkOptions.outOfNetwork")}
                    </Body>
                    {directMemberReimbursementIndicator}
                    <Shelf.Spacer />
                  </StackOrShelf>
                  <StackOrShelf spacing={spacing as Spacing} alignment="left">
                    <Shelf spacing="mini">
                      <Body>
                        <Semibold>
                          <TextColor appearance={Color.DarkGrey[400]}>{translate("CLAIMS.claimsDetailsPage.id")}:</TextColor>
                        </Semibold>
                      </Body>
                      <Body>{String(claimId).padStart(3, "0")}</Body>
                    </Shelf>
                    <Shelf spacing="mini">
                      <Body>
                        <Semibold>
                          <TextColor appearance={Color.DarkGrey[400]}>{translate("CLAIMS.claimsPage.serviceDate")}:</TextColor>
                        </Semibold>
                      </Body>
                      <Body>{formattedServiceDate}</Body>
                    </Shelf>
                    <Shelf spacing="mini">
                      <Body>
                        <Semibold>
                          <TextColor appearance={Color.DarkGrey[400]}>{translate("CLAIMS.claimListItem.receivedDate")}:</TextColor>
                        </Semibold>
                      </Body>
                      <Body>{formattedReceivedDate}</Body>
                    </Shelf>
                    <Shelf spacing="mini">
                      <Body>
                        <Semibold>
                          <TextColor appearance={Color.DarkGrey[400]}>{translate("CLAIMS.claimListItem.status")}:</TextColor>
                        </Semibold>
                      </Body>
                      <Body>{statusTagElement}</Body>
                    </Shelf>
                  </StackOrShelf>
                </LoadingGroup.Placeholder>
              </LoadingGroup>
            </Stack>
            <Shelf.Spacer />
            <Button testId="generate-claim-details-pdf-button" appearance="text" icon="print" onClick={handlePrintButtonClick}>
              {translate("CLAIMS.claimsDetailsPage.printPage")}
            </Button>
          </StackOrShelf>
        }
      >
        <Grid>
          <Grid.Span columns={cardColumns}>
            <Stack spacing="large">
              <LoadingGroup loading={isLoadingMedicalClaimData}>
                <LoadingGroup.Placeholder
                  fallback={
                    <>
                      <Stack>
                        <LoadingSkeleton style={{ height: "24px", width: "150px" }} />
                        <Panel appearance="primary" className="member-portal-panel">
                          <Stack>
                            <LoadingSkeleton style={{ height: "20px", width: "100%" }} />
                            <LoadingSkeleton style={{ height: "20px", width: "75%" }} />
                            <HorizontalLine />
                            <LoadingSkeleton style={{ height: "20px", width: "100%" }} />
                            <LoadingSkeleton style={{ height: "20px", width: "75%" }} />
                            <HorizontalLine />
                            <LoadingSkeleton style={{ height: "24px", width: "100%" }} />
                          </Stack>
                        </Panel>
                      </Stack>
                      <Stack>
                        <LoadingSkeleton style={{ height: "24px", width: "150px" }} />
                        <LoadingSkeleton style={{ height: "150px", width: "100%" }} />
                        <LoadingSkeleton style={{ height: "150px", width: "100%" }} />
                      </Stack>
                    </>
                  }
                >
                  {medicalClaimData?.status !== "pended" && (
                    <Stack>
                      <Heading>{translate("CLAIMS.claimsDetailsPage.paymentSummary")}</Heading>
                      <Panel appearance="primary" className="member-portal-panel">
                        <Stack>
                          <Shelf>
                            <Body>{translate("CLAIMS.claimsDetailsPage.totalAmountBilled")}</Body>
                            <Shelf.Spacer />
                            {formatDollars(totalBilled)}
                          </Shelf>
                          {medicalClaimData?.status !== "denied" && (
                            <Shelf>
                              <Body>{translate("CLAIMS.claimsDetailsPage.totalAllowedByPlan")}</Body>
                              <Shelf.Spacer />
                              {formatDollars(totalAllowedByPlan)}
                            </Shelf>
                          )}
                          <HorizontalLine />
                          <Shelf>
                            <Body>{translate("CLAIMS.claimsDetailsPage.totalPlanPays")}</Body>
                            <Shelf.Spacer />-{formatDollars(totalPlanPaid)}
                          </Shelf>
                          {medicalClaimData?.status === "paid" && (
                            <Shelf>
                              <Body>{translate("CLAIMS.claimsDetailsPage.deductible")}</Body>
                              <Shelf.Spacer />
                              {formatDollars(medicalClaimData?.total_amount_applied_to_deductible || "0")}
                            </Shelf>
                          )}
                          {medicalClaimData?.status !== "denied" && (
                            <Shelf>
                              <Body>{translate("CLAIMS.claimsDetailsPage.copayCoinsurance")}</Body>
                              <Shelf.Spacer />
                              {formatDollars(copayCoinsuranceAmount || "0")}
                            </Shelf>
                          )}
                          <HorizontalLine />
                          <Shelf>
                            <Subheading>{translate("CLAIMS.claimsDetailsPage.patientResponsibility")}</Subheading>
                            <Shelf.Spacer />
                            <Subheading>{formatDollars(totalPatientPay)}</Subheading>
                          </Shelf>
                        </Stack>
                      </Panel>
                    </Stack>
                  )}
                  {!!medicalClaimData?.service_line_items?.length && (
                    <Stack>
                      <Heading>{translate("CLAIMS.claimsDetailsPage.services")}</Heading>
                      <Stack>
                        {medicalClaimData?.service_line_items.map((service, index) => (
                          <ClaimsDetailServicePanel key={index} service={service} />
                        ))}
                      </Stack>
                    </Stack>
                  )}
                </LoadingGroup.Placeholder>
              </LoadingGroup>
            </Stack>
          </Grid.Span>
          <Grid.Span columns={cardColumns}>
            <Stack spacing="large">
              <LoadingGroup loading={isLoadingMedicalClaimData}>
                <LoadingGroup.Placeholder
                  fallback={
                    <>
                      <Stack>
                        <LoadingSkeleton style={{ height: "24px", width: "150px" }} />
                        <Panel appearance="primary" className="member-portal-panel">
                          <Stack>
                            <Stack spacing="mini" alignment="left">
                              <LoadingSkeleton style={{ height: "20px", width: "100px" }} />
                              <LoadingSkeleton style={{ height: "20px", width: "175px" }} />
                            </Stack>
                            <Stack spacing="mini" alignment="left">
                              <LoadingSkeleton style={{ height: "20px", width: "100px" }} />
                              <LoadingSkeleton style={{ height: "20px", width: "175px" }} />
                            </Stack>
                            <Stack spacing="mini" alignment="left">
                              <LoadingSkeleton style={{ height: "20px", width: "100px" }} />
                              <LoadingSkeleton style={{ height: "20px", width: "175px" }} />
                            </Stack>
                          </Stack>
                        </Panel>
                      </Stack>
                    </>
                  }
                >
                  <Stack>
                    <Heading>{translate("CLAIMS.claimsDetailsPage.providerDetails")}</Heading>
                    <Panel appearance="primary" className="member-portal-panel">
                      <Grid>
                        <Grid.Span columns={stackColumns}>
                          <ClaimInfoStack title={translate("CLAIMS.claimsDetailsPage.renderingProvider")} body={renderingProviderName} />
                        </Grid.Span>
                        <Grid.Break />
                        <Grid.Span columns={stackColumns}>
                          <ClaimInfoStack
                            title={translate("CLAIMS.claimsDetailsPage.renderingProviderPhone")}
                            body={
                              medicalClaimData?.rendering_provider_phone_number ? (
                                <Link to={`tel:${medicalClaimData?.rendering_provider_phone_number}`}>
                                  {medicalClaimData?.rendering_provider_phone_number}
                                </Link>
                              ) : (
                                "-"
                              )
                            }
                          />
                        </Grid.Span>
                        <Grid.Span columns={stackColumns}>
                          <ClaimInfoStack
                            title={translate("CLAIMS.claimsDetailsPage.renderingProviderAddress")}
                            body={renderingProviderAddressLink || "-"}
                          />
                        </Grid.Span>
                        <Grid.Span columns={stackColumns}>
                          <ClaimInfoStack
                            title={translate("CLAIMS.claimsDetailsPage.serviceFacilityPhone")}
                            body={
                              medicalClaimData?.service_facility_location_address_phone_number ? (
                                <Link to={`tel:${medicalClaimData?.service_facility_location_address_phone_number}`}>
                                  {medicalClaimData?.service_facility_location_address_phone_number}
                                </Link>
                              ) : (
                                "-"
                              )
                            }
                          />
                        </Grid.Span>
                        <Grid.Span columns={stackColumns}>
                          <ClaimInfoStack
                            title={translate("CLAIMS.claimsDetailsPage.serviceFacilityAddress")}
                            body={serviceFacilityAddressLink || "-"}
                          />
                        </Grid.Span>
                        {serviceFacilityAddressQuery && (
                          <Grid.Span columns={12}>
                            <Stack spacing="mini">
                              <AppleMap
                                style={{ height: "288px", minHeight: "unset" }}
                                query={{
                                  query: serviceFacilityAddressQuery,
                                  title: renderingProviderName || "Service Facility",
                                }}
                              />
                              <Body>
                                <TextColor appearance={Color.DarkGrey[400]}>
                                  {translate("CLAIMS.claimsDetailsPage.serviceFacilityLocation")}
                                </TextColor>
                              </Body>
                            </Stack>
                          </Grid.Span>
                        )}
                      </Grid>
                    </Panel>
                  </Stack>
                </LoadingGroup.Placeholder>
              </LoadingGroup>
              <LoadingGroup loading={isLoadingMedicalClaimData}>
                <LoadingGroup.Placeholder
                  fallback={
                    <>
                      <Stack>
                        <LoadingSkeleton style={{ height: "24px", width: "150px" }} />
                        <Panel appearance="primary" className="member-portal-panel">
                          <Stack>
                            <Stack spacing="mini" alignment="left">
                              <LoadingSkeleton style={{ height: "20px", width: "100px" }} />
                              <LoadingSkeleton style={{ height: "20px", width: "175px" }} />
                            </Stack>
                            <Stack spacing="mini" alignment="left">
                              <LoadingSkeleton style={{ height: "20px", width: "100px" }} />
                              <LoadingSkeleton style={{ height: "20px", width: "175px" }} />
                            </Stack>
                            <Stack spacing="mini" alignment="left">
                              <LoadingSkeleton style={{ height: "20px", width: "100px" }} />
                              <LoadingSkeleton style={{ height: "20px", width: "175px" }} />
                            </Stack>
                          </Stack>
                        </Panel>
                      </Stack>
                    </>
                  }
                >
                  <Stack>
                    <Heading>{translate("CLAIMS.claimsDetailsPage.billingDetails")}</Heading>
                    <Panel appearance="primary" className="member-portal-panel">
                      <Grid>
                        <Grid.Span columns={stackColumns}>
                          <ClaimInfoStack title={translate("CLAIMS.claimsDetailsPage.billingProvider")} body={billingProviderName} />
                        </Grid.Span>
                        <Grid.Span columns={stackColumns}>
                          <ClaimInfoStack
                            title={translate("CLAIMS.claimsDetailsPage.billingProviderPhone")}
                            body={
                              medicalClaimData?.billing_provider_phone_number ? (
                                <Link to={`tel:${medicalClaimData?.billing_provider_phone_number}`}>
                                  {medicalClaimData?.billing_provider_phone_number}
                                </Link>
                              ) : (
                                "-"
                              )
                            }
                          />
                        </Grid.Span>
                        <Grid.Span columns={stackColumns}>
                          <ClaimInfoStack
                            title={translate("CLAIMS.claimsDetailsPage.billingProviderAddress")}
                            body={billingAddress || "-"}
                          />
                        </Grid.Span>
                      </Grid>
                    </Panel>
                  </Stack>
                </LoadingGroup.Placeholder>
              </LoadingGroup>
            </Stack>
          </Grid.Span>
        </Grid>
      </ContentPanel>
    </PageWrapper>
  );
};
